// Author(s): Dave Mills, Luke Golding, Ryan O'Neill
// description: Meeting request page that proxies user details to Zapier for automated session creation
// license: Proprietary

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

function RequestMeetingPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const submitRequest = async () => {
            try {
                // Extract query parameters
                const assessorCalendarId = searchParams.get('assessorCalendarId');
                const firstName = searchParams.get('firstName');
                const lastName = searchParams.get('lastName');
                const email = searchParams.get('email');
                const hubspotId = searchParams.get('hubspotId');

                // Validate parameters exist
                if (!assessorCalendarId || !firstName || !lastName || !email || !hubspotId) {
                    setStatus('error');
                    setErrorMessage('Missing required information. Please contact your assessor directly.');
                    return;
                }

                // Call backend API

                //const apiUrl = 'http://localhost:3001';
                const apiUrl = 'https://api.meeting.csttraining.online';
                const queryString = new URLSearchParams({
                    assessorCalendarId,
                    firstName,
                    lastName,
                    email,
                    hubspotId
                }).toString();

                const response = await fetch(`${apiUrl}/api/request-meeting?${queryString}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                const data = await response.json();

                if (response.ok && data.success && data.guid) {
                    // Session created successfully, redirect to booking page
                    setStatus('success');
                    // Brief delay to show success message, then redirect
                    setTimeout(() => {
                        navigate(`/?key=${data.guid}`);
                    }, 1500);
                } else {
                    setStatus('error');
                    setErrorMessage(data.error || 'Failed to submit meeting request. Please contact your assessor directly.');
                }
            } catch (error) {
                console.error('Error submitting meeting request:', error);
                setStatus('error');
                setErrorMessage('An unexpected error occurred. Please contact your assessor directly.');
            }
        };

        submitRequest();
    }, [searchParams]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                {status === 'loading' && (
                    <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 text-center animate-fade-in">
                        <div className="flex justify-center mb-6">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-4">
                            Creating Your Session
                        </h1>
                        <p className="text-gray-300 text-lg">
                            Please wait while we create your booking session...
                        </p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 text-center animate-fade-in">
                        <div className="flex justify-center mb-6">
                            <div className="bg-green-500 rounded-full p-4">
                                <svg
                                    className="w-16 h-16 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-4">
                            Session Created!
                        </h1>
                        <p className="text-gray-300 text-lg mb-6">
                            Your booking session has been created successfully.
                        </p>
                        <div className="bg-blue-900/50 rounded-lg p-6 border border-blue-700">
                            <p className="text-blue-200 text-base">
                                Redirecting you to the booking page...
                            </p>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 text-center animate-fade-in">
                        <div className="flex justify-center mb-6">
                            <div className="bg-red-500 rounded-full p-4">
                                <svg
                                    className="w-16 h-16 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-4">
                            Request Failed
                        </h1>
                        <p className="text-gray-300 text-lg mb-6">
                            We were unable to submit your meeting request.
                        </p>
                        <div className="bg-red-900/50 rounded-lg p-6 border border-red-700 mb-6">
                            <p className="text-red-200 text-base">
                                {errorMessage}
                            </p>
                        </div>
                        <div className="bg-gray-700 rounded-lg p-6">
                            <p className="text-gray-300 text-base font-semibold mb-2">
                                What to do next:
                            </p>
                            <p className="text-gray-400 text-sm">
                                Please contact your assessor directly to schedule your meeting. 
                                They will be able to assist you with booking an appointment.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default RequestMeetingPage;
